import { useDeferredValue, useEffect, useMemo } from 'react';

import { useService } from 'react-reservice';

import { EOrderType, IOrder, IPendingOrder } from '@aqx-core/common/api/order';
import { EColumnPreferenceOrderType } from '@aqx-core/common/api/user-account-setting';

import { useCopyTradeMissedTrades } from '../copy-trade/asset';
import { useOrderSymbolPrices } from '../symbol-price';
import { useSymbolDetailId } from '../trade/analysis';
import { pickProperties } from '../utils';
import { IOrderListingContext, orderListingService } from './service';
import { IUseLivePriceParams } from './types';
import { applyFilterAndSortPositions, getActiveFilterState } from './utils';

export const useOrders = () => useService(orderListingService, (context) => context);

export const useOrdersWithKeys = <K extends keyof IOrderListingContext>(keys: K[]) => {
  return useService(orderListingService, (context) => {
    if (!context) {
      return {} as Pick<IOrderListingContext, K>;
    }
    return pickProperties(context, keys);
  });
};

export const useIsShowAllSymbol = () => useService(orderListingService, (context) => context.isShowAllSymbol);

export const useOrderHistoryDateRange = () =>
  useService(orderListingService, (context) => context.orderHistoryDateRange);

// Function to determine which hook to use based on EColumnPreferenceOrderType
export const useOpenPositionOrPendingOrder = (
  symbol: string,
  columnPreferenceOrderType: EColumnPreferenceOrderType
) => {
  return useService(orderListingService, (context) => {
    switch (columnPreferenceOrderType) {
      case EColumnPreferenceOrderType.OPEN_POSITION:
        return context.mappedOpenPositions[symbol] || ({} as IOrder);
      case EColumnPreferenceOrderType.PENDING_ORDER:
        return (
          Object.values(context.mappedPendingOrders).find((item) => item.symbol === symbol) || ({} as IPendingOrder)
        );
      default:
        return context.mappedOpenPositions[symbol] || ({} as IOrder);
    }
  });
};

export const useOpenPositionIds = () => useService(orderListingService, (context) => context.openPositionIds);
export const useOpenPositions = (id: string) =>
  useService(orderListingService, (context) => context.mappedOpenPositions[id] || ({} as IOrder));

export const useOpenPositionTableDisplay = () => {
  const symbolDetailId = useSymbolDetailId();
  return useService(orderListingService, (context) => {
    const { isShowAllSymbol, isTradePage, openPositionsTableDisplay } = context || {};
    if (!isTradePage) {
      // For asset page, we will show all orders
      return openPositionsTableDisplay;
    }
    const filteredDisplayData = openPositionsTableDisplay.filter((order) => {
      return !isShowAllSymbol ? order.symbol === symbolDetailId : true;
    });
    return filteredDisplayData;
  });
};

export const usePendingOrderIds = () => useService(orderListingService, (context) => context.pendingOrderIds);
export const usePendingOrders = (id: string) =>
  useService(orderListingService, (context) => context.mappedPendingOrders[id] || ({} as IPendingOrder));

export const usePendingOrderTableDisplay = () => {
  const symbolDetailId = useSymbolDetailId();
  return useService(orderListingService, (context) => {
    const { isShowAllSymbol, isTradePage, pendingOrdersTableDisplay } = context || {};
    if (!isTradePage) {
      return pendingOrdersTableDisplay;
    }
    const filteredDisplayData = pendingOrdersTableDisplay.filter((order) => {
      return !isShowAllSymbol ? order.symbol === symbolDetailId : true;
    });
    return filteredDisplayData;
  });
};

export const useOrderHistoryIds = () => useService(orderListingService, (context) => context.orderHistoryIds);
export const useOrderHistories = (id: string) =>
  useService(orderListingService, (context) => context.mappedOrderHistories[id] || {});

export const useOrderHistoryTableDisplay = () => {
  const symbolDetailId = useSymbolDetailId();
  return useService(orderListingService, (context) => {
    const { isShowAllSymbol, isTradePage, orderHistoryTableDisplay } = context || {};
    if (!isTradePage) {
      return orderHistoryTableDisplay;
    }
    const filteredDisplayData = orderHistoryTableDisplay.filter((order) => {
      return !isShowAllSymbol ? order.symbol === symbolDetailId : true;
    });
    return filteredDisplayData;
  });
};

export const useGetOpenPositionsAndPendingOrdersForPriceLadder = (id?: string) => {
  return useService(orderListingService, (context) => {
    const openPositions: IOrder[] = [];
    const pendingOrders: IPendingOrder[] = [];

    if (id) {
      Object.values(context.mappedOpenPositions).forEach((o) => {
        if (o.symbol === id) {
          openPositions.push(o);
        }
      });

      Object.values(context.mappedPendingOrders).forEach((o) => {
        if (o.symbol === id) {
          pendingOrders.push(o);
        }
      });
    }

    return { openPositions, pendingOrders };
  });
};

export const useOrderDetailQuery = () =>
  useService(orderListingService, (context) => ({
    data: context.orderDetail,
    isLoading: context.isOrderDetailLoading
  }));

export const useTradePositionQuery = () => {
  const symbolDetailId = useSymbolDetailId();
  return useService(orderListingService, (context) => {
    const { isTradePage } = context || {};

    const filterFormValues = isTradePage
      ? { ...context.filterFormValues, currentAssetType: undefined }
      : context.filterFormValues;

    return {
      openPositionsTableDisplay: context.openPositionsTableDisplay,
      pendingOrdersTableDisplay: context.pendingOrdersTableDisplay,
      orderHistoryDisplay: context.orderHistoryTableDisplay,
      marketOrderCounts: context.marketOrderCounts,
      pendingOrderCounts: context.pendingOrderCounts,
      searchText: context.searchText,
      filterFormValues,
      orderSelectedTab: context.orderSelectedTab,
      isShowAllSymbol: context.isShowAllSymbol,
      isTradePage,
      symbolDetailId
    };
  });
};

export const useTradePositionFilterSort = () => {
  const {
    filterFormValues: filterValues,
    marketOrderCounts,
    openPositionsTableDisplay,
    orderSelectedTab,
    orderHistoryDisplay,
    pendingOrderCounts,
    pendingOrdersTableDisplay,
    searchText,
    isShowAllSymbol,
    isTradePage,
    symbolDetailId
  } = useTradePositionQuery();
  const copyTradeMissedTrades = useCopyTradeMissedTrades();
  const searchKey = searchText ? searchText.toLowerCase() : '';
  const shouldFilterBySymbol = isTradePage && !isShowAllSymbol;

  const deferredOpenPositions = useDeferredValue(openPositionsTableDisplay);
  const deferredPendingOrders = useDeferredValue(pendingOrdersTableDisplay);
  const deferredOrderHistory = useDeferredValue(orderHistoryDisplay);

  const openPositions = useMemo(
    () =>
      applyFilterAndSortPositions(
        shouldFilterBySymbol ? deferredOpenPositions.filter((o) => o.symbol === symbolDetailId) : deferredOpenPositions,
        searchKey,
        filterValues,
        'openTime',
        'orderId'
      ),
    [deferredOpenPositions, searchKey, filterValues, shouldFilterBySymbol, symbolDetailId]
  );

  const pendingOrders = useMemo(
    () =>
      applyFilterAndSortPositions(
        shouldFilterBySymbol ? deferredPendingOrders.filter((o) => o.symbol === symbolDetailId) : deferredPendingOrders,
        searchKey,
        filterValues,
        'openTime',
        'openTime'
      ),
    [deferredPendingOrders, searchKey, filterValues, shouldFilterBySymbol, symbolDetailId]
  );

  useEffect(() => {
    orderListingService.setFilteredOpenPositions(openPositions);
  }, [openPositions]);

  useEffect(() => {
    orderListingService.setFilteredPendingOrders(pendingOrders);
  }, [pendingOrders]);

  const isHistoryTab =
    orderSelectedTab === EColumnPreferenceOrderType.ORDER_HISTORY ||
    orderSelectedTab === EColumnPreferenceOrderType.POSITION_HISTORY ||
    orderSelectedTab === EColumnPreferenceOrderType.ORDERS_AND_DEALS ||
    orderSelectedTab === EColumnPreferenceOrderType.TRANSACTION_HISTORY;

  const orderHistories = useMemo(() => {
    if (!isHistoryTab) {
      return [];
    }
    const filtered = shouldFilterBySymbol
      ? deferredOrderHistory.filter((o) => o.symbol === symbolDetailId)
      : deferredOrderHistory;
    // We use API for filter date range with history orders, so we need to remove date range from filterValues when filter and sort on FE to avoid duplicated filter by date range
    const filterValuesWithoutDateRange = filterValues ? { ...filterValues, dateRange: undefined } : filterValues;
    const result = applyFilterAndSortPositions(
      filtered,
      searchKey,
      filterValuesWithoutDateRange,
      'closeTime',
      'openTime'
    );
    return result;
  }, [isHistoryTab, deferredOrderHistory, searchKey, filterValues, shouldFilterBySymbol, symbolDetailId]);

  const isFilterActive = filterValues ? getActiveFilterState(filterValues).isFilterActive : false;

  const counts = useMemo(() => {
    return searchKey || isFilterActive
      ? {
          marketOrderCounts: openPositions.length,
          pendingOrderCounts: pendingOrders.length
        }
      : {
          marketOrderCounts,
          pendingOrderCounts
        };
  }, [searchKey, isFilterActive, openPositions.length, pendingOrders.length, marketOrderCounts, pendingOrderCounts]);

  switch (orderSelectedTab) {
    case EColumnPreferenceOrderType.OPEN_POSITION:
      return {
        data: openPositions,
        ...counts
      };
    case EColumnPreferenceOrderType.PENDING_ORDER:
      return {
        data: pendingOrders,
        ...counts
      };
    case EColumnPreferenceOrderType.ORDER_HISTORY:
    case EColumnPreferenceOrderType.POSITION_HISTORY:
    case EColumnPreferenceOrderType.ORDERS_AND_DEALS:
    case EColumnPreferenceOrderType.TRANSACTION_HISTORY:
      return {
        data: orderHistories,
        ...counts
      };
    case EColumnPreferenceOrderType.COPY_TRADE_MISSED_TRADES: {
      const filteredMissedTrades = searchKey
        ? copyTradeMissedTrades.filter((trade) => trade.strategyName?.toLowerCase().includes(searchKey))
        : copyTradeMissedTrades;
      return {
        data: filteredMissedTrades,
        ...counts
      };
    }
    default:
      return {
        data: [],
        ...counts
      };
  }
};

export const useFilteredOpenPositions = () =>
  useService(orderListingService, (context) => context.filteredOpenPositions);

export const useFilteredPendingOrders = () =>
  useService(orderListingService, (context) => context.filteredPendingOrders);

export const useTradePositionQueryLoading = () =>
  useService(orderListingService, (context) => {
    switch (context.orderSelectedTab) {
      case EColumnPreferenceOrderType.OPEN_POSITION:
        return context.openPositionLoading;
      case EColumnPreferenceOrderType.PENDING_ORDER:
        return context.pendingOrderLoading;
      case EColumnPreferenceOrderType.ORDER_HISTORY:
      case EColumnPreferenceOrderType.POSITION_HISTORY:
      case EColumnPreferenceOrderType.ORDERS_AND_DEALS:
      case EColumnPreferenceOrderType.TRANSACTION_HISTORY:
        return context.orderHistoryLoading;
      default:
        return false;
    }
  });

export const useOrderLivePrice = ({ symbol, orderType, isOpenPosition, orderCurrentPrice }: IUseLivePriceParams) => {
  const { liveBuy, liveSell } = useOrderSymbolPrices(symbol);
  const { buy, sell } = orderCurrentPrice || {};

  const liveBuyOrDefault = useMemo(() => (buy !== undefined ? liveBuy || buy : liveBuy), [buy, liveBuy]);
  const liveSellOrDefault = useMemo(() => (sell !== undefined ? liveSell || sell : liveSell), [sell, liveSell]);

  const livePrice = useMemo(() => {
    switch (orderType) {
      case EOrderType.SELL:
      case EOrderType.SELL_STOP:
      case EOrderType.SELL_LIMIT:
      case EOrderType.SELL_STOP_LIMIT:
        return isOpenPosition ? liveBuyOrDefault : liveSellOrDefault;
      default:
        return isOpenPosition ? liveSellOrDefault : liveBuyOrDefault;
    }
  }, [orderType, isOpenPosition, liveBuyOrDefault, liveSellOrDefault]);

  return livePrice;
};

export const useFilterValues = () => useService(orderListingService, (context) => context.filterFormValues);
export const useActiveOrderListTab = () => {
  return useService(orderListingService, (context) => context.orderSelectedTab);
};
